<template>
  <v-card>
    <v-card-title>
      Forest
      <v-spacer></v-spacer>
      <v-menu v-model="openActions" offset-y>
        <template v-slot:activator="{ on }">
          <v-btn v-show="isNotGuest" v-on="on" color="primary">Actions</v-btn>
        </template>

        <v-list>
          <v-list-item>
            <v-list-item-title>
              <v-btn class="ma-2" text color="primary" @click="checkTrees"
                >Check Trees</v-btn
              >
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>
    <v-simple-table>
      <tbody>
        <tr>
          <td style="width: 300px">ID</td>
          <td>
            <span>{{ forest.id }}</span>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  class="ma-2"
                  text
                  icon
                  v-if="forest.id"
                  @click.prevent="copyLink(forest.id)"
                >
                  <img
                    svg-inline
                    src="../../assets/images/ico-copy.svg"
                    alt=""
                  />
                </v-btn>
              </template>
              <span>Copy</span>
            </v-tooltip>
          </td>
        </tr>
        <tr>
          <td>Name</td>
          <td>{{ forest.name }}</td>
        </tr>
        <tr>
          <td>URL</td>
          <td>
            <a
              :href="`${$options.FOREST_DOMAIN}/${forest.slug}`"
              target="_blank"
              >{{ `${$options.FOREST_DOMAIN}/${forest.slug}` }}</a
            >
          </td>
        </tr>
        <tr>
          <td>Company</td>
          <td>
            <router-link
              :to="{ name: 'Company', params: { id: forest.ownedBy.id } }"
              ><span ref="companyId">
                {{ forest.ownedBy.name }}
              </span></router-link
            >
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  class="ma-2"
                  text
                  icon
                  v-if="forest.ownedBy.id"
                  @click.prevent="copyCompanyId(forest.ownedBy.id)"
                >
                  <img
                    svg-inline
                    src="../../assets/images/ico-copy.svg"
                    alt=""
                  />
                </v-btn>
              </template>
              <span>Copy</span>
            </v-tooltip>
          </td>
        </tr>
        <tr>
          <td>Type</td>
          <td>{{ forest.type }}</td>
        </tr>
        <tr>
          <td>Status</td>
          <td>{{ forest.active ? "Active" : "Deactivated" }}</td>
        </tr>
        <tr>
          <td>Created</td>
          <td>{{ forest.createdAt }}</td>
        </tr>
        <template v-if="forest.type === $options.FOREST_TYPES.SINGLE">
          <tr>
            <td>Platforms order</td>
            <td>
              <platforms-logo
                :platforms="forest.platformsOrder"
              ></platforms-logo>
            </td>
          </tr>
          <tr>
            <td>Pinned platform</td>
            <td>
              <platform-logo
                v-if="forest.pinnedPlatform"
                :platform="forest.pinnedPlatform"
              ></platform-logo>
              <template v-else>-</template>
            </td>
          </tr>
          <tr>
            <td>Tree limit</td>
            <td>
              {{
                forest.plantingReviewLimit === -1
                  ? "Unlimited"
                  : forest.plantingReviewLimit
              }}
            </td>
          </tr>
          <tr>
            <td>Planting Project</td>
            <td>
              {{
                $options.PROJECT_TYPES.find(
                  (p) => Number(p.id) === Number(forest.projectId)
                ).name
              }}
            </td>
          </tr>
        </template>
        <tr>
          <td>Review amount</td>
          <td>{{ forest.reviewAmount }}</td>
        </tr>
        <tr>
          <td>Planted trees</td>
          <td>
            <v-simple-table>
              <tbody>
                <tr>
                  <td style="width: 150px">For reviews</td>
                  <td>{{ forest.reviewTreeAmount }}</td>
                </tr>
                <tr>
                  <td>Additional trees</td>
                  <td>{{ forest.additionalTreeAmount }}</td>
                </tr>
                <tr>
                  <td>Total</td>
                  <td>{{ forest.totalTreeAmount }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </td>
        </tr>
        <tr>
          <td>Rating</td>
          <td>{{ forest.score }}</td>
        </tr>
        <tr>
          <td>Crawl requests</td>
          <td>
            <router-link
              :to="{
                name: 'ForestCrawlRequests',
                params: { id: forest.id, name: forest.name },
              }"
              >{{ forestCrawlRequests }}</router-link
            >
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <!-- Success message snackbar -->
    <success-snackbar v-model="showSuccessMessage" :message="successMessage" />
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { API } from "../../api";

import { FOREST_TYPES, PROJECT_TYPES } from "../../constants";
import PlatformLogo from "../Base/PlatformLogo.vue";
import PlatformsLogo from "../Base/PlatformsLogo.vue";
import SuccessSnackbar from "../Base/SuccessSnackbar.vue";
import { writeToClipboard } from "../../utils/copyText";

export default {
  name: "ForestProfile",
  FOREST_DOMAIN: process.env.VUE_APP_FOREST_DOMAIN,
  PROJECT_TYPES,
  FOREST_TYPES,
  components: {
    PlatformsLogo,
    PlatformLogo,
    SuccessSnackbar,
  },
  props: {
    forest: {
      type: Object,
      required: true,
    },
    forestCrawlRequests: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      openActions: false,
      isLoading: false,
      showSuccessMessage: false,
      successMessage: "",
    };
  },
  computed: {
    ...mapGetters(["isNotGuest", "isProd"]),
  },
  methods: {
    async copyLink(id) {
      await writeToClipboard(String(id));
      this.successMessage = "Forest ID successfully copied to clipboard!";
      this.showSuccessMessage = true;
    },
    async copyCompanyId(id) {
      await writeToClipboard(String(id));
      this.successMessage = "Company ID successfully copied to clipboard!";
      this.showSuccessMessage = true;
    },
    async checkTrees() {
      this.isLoading = true;
      try {
        await API.checkTrees({
          forestId: this.forest.id,
          forceReport: true,
        });
      } catch (error) {
        console.error("Failed to check trees:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
